// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { ChoiceContext, GetChoiceContextRequest } from '@digitalasset/allocation-api-ts-client';
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import { TransferInstruction } from '@daml.js/splice-api-token-transfer-instruction/lib/Splice/Api/Token/TransferInstructionV1';
import { ContractId } from '@daml/types';
import { useTransferInstructionApiClient } from '../../../contexts/TransferInstructionApiContext';

export enum TransferInstructionContextType {
  Accept,
  Reject,
  Withdraw,
}

interface ContextMap {
  [TransferInstructionContextType.Accept]: (
    transferInstructionCid: string,
    registrar: string,
  ) => Promise<ChoiceContext>;
  [TransferInstructionContextType.Reject]: (
    transferInstructionCid: string,
    registrar: string,
  ) => Promise<ChoiceContext>;
  [TransferInstructionContextType.Withdraw]: (
    transferInstructionCid: string,
    registrar: string,
  ) => Promise<ChoiceContext>;
}
export const useTransferInstructionContext: (
  contextType: TransferInstructionContextType,
) => UseMutationResult<
  ChoiceContext,
  string,
  {
    registrar: string;
    transferInstructionCid: ContractId<TransferInstruction>;
  }
> = (contextType: TransferInstructionContextType) => {
  const transferInstructionApi = useTransferInstructionApiClient();
  const getChoiceContextRequest: GetChoiceContextRequest = { meta: {} };
  const reject = (transferInstructionCid: string, registrar: string) =>
    transferInstructionApi.getTransferInstructionRejectContext(
      transferInstructionCid,
      registrar,
      getChoiceContextRequest,
    );
  const accept = (transferInstructionCid: string, registrar: string) => {
    return transferInstructionApi.getTransferInstructionAcceptContext(
      transferInstructionCid,
      registrar,
      getChoiceContextRequest,
    );
  };

  const withdraw = (transferInstructionCid: string, registrar: string) =>
    transferInstructionApi.getTransferInstructionWithdrawContext(
      transferInstructionCid,
      registrar,
      getChoiceContextRequest,
    );
  const contextMap: ContextMap = {
    [TransferInstructionContextType.Accept]: accept,
    [TransferInstructionContextType.Reject]: reject,
    [TransferInstructionContextType.Withdraw]: withdraw,
  };
  return useMutation({
    mutationKey: ['transferInstructionContext', transferInstructionApi],
    mutationFn: async ({ registrar, transferInstructionCid }) => {
      return contextMap[contextType](transferInstructionCid, registrar);
    },
  });
};
